// NQDashboard.jsx - Separate sections for each reference signal

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { sameOriginWs } from '../../utils/wsUrl';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Grid,
  alpha,
  Container,
  CircularProgress,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
  Circle as CircleIcon,
  RadioButtonChecked as DotIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';

const TV = {
  bg: {
    primary: '#131722',
    secondary: '#1E222D',
    tertiary: '#2A2E39',
    card: '#161A25',
    hover: '#1E222D',
  },
  border: {
    subtle: 'rgba(42, 46, 57, 0.5)',
    medium: 'rgba(42, 46, 57, 0.8)',
    bright: 'rgba(134, 137, 147, 0.3)',
  },
  text: {
    primary: '#D1D4DC',
    secondary: '#787B86',
    tertiary: '#434651',
    white: '#FFFFFF',
  },
  chart: {
    bull: '#26a69a',
    bear: '#ef5350',
    neutral: '#9598A1',
    volume: '#363A45',
  },
  accent: {
    blue: '#2962FF',
    purple: '#9C27B0',
    cyan: '#00BCD4',
    orange: '#FF9800',
    yellow: '#FFC107',
  },
};

const CompactSignalRow = ({ signalName, timeDiff, signalPrice, signalTime, hasReference, isNew }) => {
  const isBefore = timeDiff < 0;
  const isSimultaneous = Math.abs(timeDiff) < 0.5;
  
  const getColor = () => {
    if (!hasReference) return TV.accent.yellow;
    if (isSimultaneous) return TV.chart.neutral;
    return isBefore ? TV.chart.bear : TV.chart.bull;
  };

  const color = getColor();
  const absTimeDiff = Math.abs(timeDiff);

  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: isNew ? alpha(TV.accent.blue, 0.05) : TV.bg.card,
        border: `1px solid ${isNew ? alpha(TV.accent.blue, 0.3) : TV.border.subtle}`,
        borderLeft: `2px solid ${color}`,
        borderRadius: '2px',
        px: 1,
        py: 0.75,
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        '&:hover': {
          bgcolor: TV.bg.hover,
          borderColor: TV.border.medium,
        },
      }}
    >
      <Grid container alignItems="center" spacing={0.5}>
        <Grid item xs={3.5}>
          <Stack spacing={0}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <CircleIcon sx={{ fontSize: 5, color: color }} />
              <Typography 
                sx={{ 
                  fontSize: '8px', 
                  color: TV.text.secondary,
                  fontWeight: 500,
                  letterSpacing: '0.2px',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {signalName.replace(/_/g, ' ')}
              </Typography>
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={2.5}>
          <Typography 
            sx={{ 
              fontSize: '8px', 
              color: TV.text.tertiary,
              fontFamily: 'monospace',
            }}
          >
            {new Date(signalTime).toLocaleTimeString('en-US', { 
              hour12: false,
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })}
          </Typography>
        </Grid>

        <Grid item xs={3}>
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 600,
              color: TV.text.primary,
              fontFamily: 'monospace',
              letterSpacing: '-0.2px',
            }}
          >
            ${signalPrice.toLocaleString('en-US', { 
              minimumFractionDigits: 2,
              maximumFractionDigits: 2 
            })}
          </Typography>
        </Grid>

        <Grid item xs={3}>
          <Box sx={{ textAlign: 'right' }}>
            {hasReference ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.4 }}>
                {isSimultaneous ? (
                  <RemoveIcon sx={{ fontSize: 9 }} />
                ) : isBefore ? (
                  <TrendingDownIcon sx={{ fontSize: 9, color: TV.chart.bear }} />
                ) : (
                  <TrendingUpIcon sx={{ fontSize: 9, color: TV.chart.bull }} />
                )}
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: color,
                    fontFamily: 'monospace',
                  }}
                >
                  {absTimeDiff.toFixed(2)}m
                </Typography>
              </Box>
            ) : (
              <Chip
                size="small"
                label="No Ref"
                sx={{
                  fontSize: '7px',
                  height: '16px',
                  bgcolor: alpha(TV.accent.yellow, 0.15),
                  color: TV.accent.yellow,
                  '& .MuiChip-label': {
                    px: 0.6,
                  }
                }}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const SessionPanel = ({ timeframe, tradingHour, referenceData, accentColor }) => {
  const hasReference = referenceData?.has_reference;
  const refSignal = referenceData?.reference_signal;
  const timeDifferences = referenceData?.time_differences || [];

  const sortedTimeDifferences = [...timeDifferences].sort((a, b) => {
    const timeA = new Date(a.signal_time).getTime();
    const timeB = new Date(b.signal_time).getTime();
    return timeB - timeA;
  });

  const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
  const isNewSignal = (signalTime) => new Date(signalTime).getTime() > fiveMinutesAgo;

  const isETH = tradingHour === 'ETH';
  const sessionColor = isETH ? TV.accent.purple : TV.accent.orange;

  if (!hasReference) {
    return (
      <Paper
        elevation={0}
        sx={{
          bgcolor: TV.bg.secondary,
          border: `1px solid ${TV.border.subtle}`,
          borderRadius: '3px',
          p: 1.5,
          minHeight: '150px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <WarningIcon sx={{ fontSize: 28, color: TV.accent.yellow, mb: 0.75 }} />
        <Typography 
          sx={{ 
            fontSize: '9px', 
            color: TV.text.secondary,
            textAlign: 'center',
            fontWeight: 500,
          }}
        >
          No Reference Signal
        </Typography>
        <Typography 
          sx={{ 
            fontSize: '8px', 
            color: TV.text.tertiary,
            textAlign: 'center',
            mt: 0.5,
          }}
        >
          {timeframe} • {tradingHour}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: TV.bg.secondary,
        border: `1px solid ${TV.border.subtle}`,
        borderRadius: '3px',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(accentColor, 0.12)} 0%, transparent 100%)`,
          borderBottom: `1px solid ${TV.border.subtle}`,
          p: 1,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '3px',
                bgcolor: alpha(sessionColor, 0.15),
                border: `1px solid ${alpha(sessionColor, 0.3)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '9px', fontWeight: 700, color: sessionColor }}>
                {tradingHour}
              </Typography>
            </Box>
            <Box>
              <Typography 
                sx={{ 
                  fontSize: '11px', 
                  fontWeight: 600,
                  color: TV.text.primary,
                  letterSpacing: '0.1px',
                }}
              >
                {timeframe} • {tradingHour}
              </Typography>
              <Typography 
                sx={{ 
                  fontSize: '8px', 
                  color: TV.text.tertiary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.2px',
                }}
              >
                {sortedTimeDifferences.length} signals tracked
              </Typography>
            </Box>
          </Stack>
          
          <CheckIcon 
            sx={{ 
              fontSize: 16, 
              color: TV.chart.bull,
            }} 
          />
        </Stack>
      </Box>

      {/* Reference Signal Info */}
      <Box sx={{ p: 1, borderBottom: `1px solid ${TV.border.subtle}`, bgcolor: alpha(accentColor, 0.03) }}>
        <Grid container alignItems="center" spacing={1}>
          <Grid item xs={4}>
            <Typography 
              sx={{ 
                fontSize: '7px', 
                color: TV.text.tertiary,
                textTransform: 'uppercase',
                letterSpacing: '0.2px',
                mb: 0.25,
              }}
            >
              Reference
            </Typography>
            <Typography 
              sx={{ 
                fontSize: '10px', 
                fontWeight: 600,
                color: accentColor,
                letterSpacing: '0.1px',
              }}
            >
              {refSignal.name}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography 
              sx={{ 
                fontSize: '7px', 
                color: TV.text.tertiary,
                textTransform: 'uppercase',
                letterSpacing: '0.2px',
                mb: 0.25,
              }}
            >
              Price
            </Typography>
            <Typography 
              sx={{ 
                fontSize: '10px', 
                fontWeight: 600,
                color: TV.text.white,
                fontFamily: 'monospace',
              }}
            >
              ${refSignal.price.toFixed(2)}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography 
              sx={{ 
                fontSize: '7px', 
                color: TV.text.tertiary,
                textTransform: 'uppercase',
                letterSpacing: '0.2px',
                mb: 0.25,
              }}
            >
              Time
            </Typography>
            <Typography 
              sx={{ 
                fontSize: '9px', 
                fontWeight: 500,
                color: TV.text.secondary,
                fontFamily: 'monospace',
              }}
            >
              {new Date(refSignal.time).toLocaleTimeString('en-US', { 
                hour12: false,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Column Headers */}
      <Box sx={{ px: 1, py: 0.6, borderBottom: `1px solid ${TV.border.subtle}`, bgcolor: TV.bg.tertiary }}>
        <Grid container alignItems="center" spacing={0.5}>
          <Grid item xs={3.5}>
            <Typography sx={{ fontSize: '7px', color: TV.text.tertiary, fontWeight: 600, textTransform: 'uppercase' }}>
              Signal
            </Typography>
          </Grid>
          <Grid item xs={2.5}>
            <Typography sx={{ fontSize: '7px', color: TV.text.tertiary, fontWeight: 600, textTransform: 'uppercase' }}>
              Time
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography sx={{ fontSize: '7px', color: TV.text.tertiary, fontWeight: 600, textTransform: 'uppercase' }}>
              Price
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography sx={{ fontSize: '7px', color: TV.text.tertiary, fontWeight: 600, textTransform: 'uppercase', textAlign: 'right' }}>
              Diff (min)
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Signals List */}
      <Box 
        sx={{ 
          p: 1,
          maxHeight: '300px',
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: TV.bg.tertiary,
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: TV.border.medium,
            borderRadius: '3px',
          },
        }}
      >
        {sortedTimeDifferences.length === 0 ? (
          <Typography 
            sx={{ 
              fontSize: '9px', 
              color: TV.text.tertiary,
              textAlign: 'center',
              py: 2,
            }}
          >
            No other signals to compare
          </Typography>
        ) : (
          <Stack spacing={0.6}>
            {sortedTimeDifferences.map((diff, index) => {
              const isNew = isNewSignal(diff.signal_time);
              return (
                <CompactSignalRow
                  key={`${diff.signal_name}-${index}`}
                  signalName={diff.signal_name}
                  timeDiff={diff.time_difference_minutes}
                  signalPrice={diff.signal_price}
                  signalTime={diff.signal_time}
                  hasReference={true}
                  isNew={isNew}
                />
              );
            })}
          </Stack>
        )}
      </Box>
    </Paper>
  );
};

const ReferenceSection = ({ title, accentColor, icon, data1mEth, data45sRth }) => {
  return (
    <Box>
      {/* Section Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          pb: 1.5,
          borderBottom: `2px solid ${TV.border.medium}`,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '4px',
            bgcolor: alpha(accentColor, 0.15),
            border: `2px solid ${alpha(accentColor, 0.3)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 700,
              color: accentColor,
              letterSpacing: '-0.3px',
              lineHeight: 1,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: '9px',
              color: TV.text.secondary,
              letterSpacing: '0.1px',
              mt: 0.5,
            }}
          >
            Time difference analysis
          </Typography>
        </Box>
      </Box>

      {/* Panels */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <SessionPanel
            timeframe="1m"
            tradingHour="ETH"
            referenceData={data1mEth}
            accentColor={accentColor}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SessionPanel
            timeframe="45s"
            tradingHour="RTH"
            referenceData={data45sRth}
            accentColor={accentColor}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

const NQDashboard = ({
  apiBaseUrl = window.location.origin,
  wsUrl = sameOriginWs('/ws/nq-dashboard/'),
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const pingInterval = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectDelay = 30000;

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiBaseUrl}/strategies/api/nq-dashboard/?timeframes=1m,45s`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const dashboardData = await response.json();
      setData(dashboardData);
      setLastUpdate(new Date());
      setError(null);
      
      console.log('[Dashboard] Data loaded:', {
        '1m_eth_sb12': dashboardData.timeframes?.['1m']?.eth?.smart_band_12?.has_reference || false,
        '1m_eth_sb12s': dashboardData.timeframes?.['1m']?.eth?.smart_band_12_short?.has_reference || false,
        '45s_rth_sb12': dashboardData.timeframes?.['45s']?.rth?.smart_band_12?.has_reference || false,
        '45s_rth_sb12s': dashboardData.timeframes?.['45s']?.rth?.smart_band_12_short?.has_reference || false,
      });
    } catch (err) {
      setError(`Failed to load dashboard: ${err.message}`);
      console.error('[Dashboard] Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl]);

  const connectWebSocket = useCallback(() => {
    if (ws.current?.readyState === WebSocket.OPEN || ws.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    try {
      console.log('[WS] Connecting to WebSocket...');
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        console.log('[WS] Connected');
        setConnected(true);
        setError(null);
        reconnectAttempts.current = 0;

        if (pingInterval.current) clearInterval(pingInterval.current);
        pingInterval.current = setInterval(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      ws.current.onmessage = (event) => {
        try {
          const update = JSON.parse(event.data);
          if (update.type === 'connection' || update.type === 'pong') return;
          
          console.log('[WS] Signal received - triggering refresh');
          loadDashboardData();
        } catch (e) {
          console.error('[WS] Parse error:', e);
        }
      };

      ws.current.onerror = (error) => {
        console.error('[WS] Error:', error);
        setError('WebSocket connection error');
      };

      ws.current.onclose = (event) => {
        console.log('[WS] Disconnected', event.code, event.reason);
        setConnected(false);
        
        if (pingInterval.current) {
          clearInterval(pingInterval.current);
          pingInterval.current = null;
        }

        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), maxReconnectDelay);
        reconnectAttempts.current++;
        
        if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = setTimeout(() => {
          connectWebSocket();
        }, delay);
      };
    } catch (e) {
      console.error('[WS] Connection failed:', e);
      setError('Failed to establish WebSocket connection');
      
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), maxReconnectDelay);
      reconnectAttempts.current++;
      
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = setTimeout(() => {
        connectWebSocket();
      }, delay);
    }
  }, [wsUrl, loadDashboardData]);

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      loadDashboardData();
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, [loadDashboardData]);

  useEffect(() => {
    loadDashboardData();
    connectWebSocket();

    return () => {
      if (pingInterval.current) {
        clearInterval(pingInterval.current);
        pingInterval.current = null;
      }
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }
      if (ws.current) {
        ws.current.close();
        ws.current = null;
      }
    };
  }, []);

  if (loading && !data) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          bgcolor: TV.bg.primary,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress 
            size={32} 
            thickness={2}
            sx={{ color: TV.accent.blue }} 
          />
          <Typography sx={{ color: TV.text.secondary, fontSize: '10px', letterSpacing: '0.2px' }}>
            Loading Market Data...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: TV.bg.primary }}>
      <Container maxWidth="xl" sx={{ py: 2 }}>
        <Stack spacing={3}>
          {/* Main Header */}
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 3,
                    height: 28,
                    bgcolor: TV.accent.blue,
                    borderRadius: '2px',
                    boxShadow: `0 0 8px ${alpha(TV.accent.blue, 0.5)}`,
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: '20px',
                      fontWeight: 600,
                      color: TV.text.white,
                      letterSpacing: '-0.4px',
                      lineHeight: 1,
                    }}
                  >
                    NQ Dual Reference Analysis
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '10px',
                      color: TV.text.secondary,
                      letterSpacing: '0.1px',
                      mt: 0.4,
                    }}
                  >
                    SMART_BAND_12 vs SMART_BAND_12_SHORT • Independent Tracking
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={1}>
                <Tooltip title={connected ? "Live Connection" : "Reconnecting..."} arrow>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1.2,
                      py: 0.6,
                      bgcolor: alpha(connected ? TV.chart.bull : TV.chart.bear, 0.08),
                      border: `1px solid ${alpha(connected ? TV.chart.bull : TV.chart.bear, 0.25)}`,
                      borderRadius: '2px',
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: connected ? TV.chart.bull : TV.chart.bear,
                        boxShadow: connected ? `0 0 5px ${TV.chart.bull}` : 'none',
                        animation: connected ? 'pulse 2s ease-in-out infinite' : 'none',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '9px',
                        fontWeight: 600,
                        color: connected ? TV.chart.bull : TV.chart.bear,
                        textTransform: 'uppercase',
                        letterSpacing: '0.2px',
                      }}
                    >
                      {connected ? 'Live' : 'Offline'}
                    </Typography>
                  </Box>
                </Tooltip>

                <Stack spacing={0}>
                  <Typography
                    sx={{
                      fontSize: '9px',
                      color: TV.text.tertiary,
                      fontFamily: 'monospace',
                    }}
                  >
                    {lastUpdate ? lastUpdate.toLocaleTimeString('en-US', { 
                      hour12: false 
                    }) : '--:--:--'}
                  </Typography>
                  {lastUpdate && (
                    <Typography
                      sx={{
                        fontSize: '7px',
                        color: TV.text.tertiary,
                        textAlign: 'right',
                      }}
                    >
                      Updated
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Box>

          {error && (
            <Box
              sx={{
                bgcolor: alpha(TV.chart.bear, 0.08),
                border: `1px solid ${alpha(TV.chart.bear, 0.25)}`,
                borderRadius: '2px',
                p: 1.2,
              }}
            >
              <Typography sx={{ fontSize: '10px', color: TV.chart.bear }}>
                {error}
              </Typography>
            </Box>
          )}

          {/* SMART_BAND_12 Section */}
          {data && data.timeframes && (
            <ReferenceSection
              title="SMART_BAND_12"
              accentColor={TV.accent.cyan}
              icon={
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: TV.accent.cyan }}>
                  12
                </Typography>
              }
              data1mEth={data.timeframes['1m']?.eth?.smart_band_12}
              data45sRth={data.timeframes['45s']?.rth?.smart_band_12}
            />
          )}

          {/* SMART_BAND_12_SHORT Section */}
          {data && data.timeframes && (
            <ReferenceSection
              title="SMART_BAND_12_SHORT"
              accentColor={TV.chart.bear}
              icon={
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: TV.chart.bear }}>
                  12S
                </Typography>
              }
              data1mEth={data.timeframes['1m']?.eth?.smart_band_12_short}
              data45sRth={data.timeframes['45s']?.rth?.smart_band_12_short}
            />
          )}
        </Stack>
      </Container>

      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.4;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default NQDashboard;
